/**
 * Created by rathompson on 9/14/21
 * File: AddIdentityModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Modal,
  ModalHeader,
  useModal
} from '@panwds/react-ui'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import {
  LABEL_ADD_NEW_IDENTITY,
  LABEL_IDENTITY_INFORMATION,
  LABEL_CLIENT_CREDENTIALS,
  LABEL_EDIT_ACCOUNT,
  LABEL_VIEW_IDENTITY,
  LABEL_ASSIGN_ROLES,
  ERROR_SERVER_REMOVE,
} from '../../../../constants/LabelConstants'

import Sidebar from '../../../common/modal/sidebar/Sidebar'
import IdentityInformationOld from './IdentityInformation/IdentityInformationOld'
import ClientCredentials from './ClientCredentials/ClientCredentials'
import RemoveIdentityConfirmationModal from './RemoveIdentityConfirmationModal'
import AssignRolesOld from './AssignRolesOld'
import AssignRoles from './AssignRoles'

import { IDENTITY_TYPES } from '../../../../constants/IAMConstants'
import { getVarValue } from '../../../../../common/utils/Sparky'

import '../../../../styles/Modals.scss'
import './IdentityModal.scss'
import { FF_IAM_PHASE_1, FF_IAM_PHASE_2, FF_ROLES_ENABLED } from '../../../../../common/utils/FeatureFlags'
import IdentityInformation from './IdentityInformation/IdentityInformation'

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)
const iamPhase1 = getVarValue(FF_IAM_PHASE_1, false)
const iamPhase2 = getVarValue(FF_IAM_PHASE_2, false)

const SIDEBAR_OPTIONS = {
  IDENTITY_INFORMATION: {
    name: LABEL_IDENTITY_INFORMATION,
  },
  CLIENT_CREDENTIALS: {
    name: LABEL_CLIENT_CREDENTIALS,
  }
}
if (rolesEnabled) {
  SIDEBAR_OPTIONS.ASSIGN_ROLES = {
    name: LABEL_ASSIGN_ROLES,
  }
}

/**
 *
 * @param closeModal callback for close/cancel buttons
 * @param isOpen boolean indicating whether modal is visible or not
 * @param tenant currently selected tenant
 * @param getData callback to refresh identity table
 * @returns {JSX.Element}
 * @constructor
 * @desc Form modal that adds a new identity to the currently selected tenant
 */
const IdentityModal = ({
  closeModal: _closeModal,
  isOpen,
  tenant,
  getData,
  member: _member,
  isEdit,
  removeIdentity: _removeIdentity,
  length,
  sidebarOption
}) => {

  /**
   *     ========= Local State ===========
   */
  const [sidebarSelection, setSidebarSelection] = useState(sidebarOption || SIDEBAR_OPTIONS.IDENTITY_INFORMATION)
  const [member, setMember] = useState(_member)
  const [identityType, setIdentityType] = useState(_member?.identity_type)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const removeModal = useModal(RemoveIdentityConfirmationModal)

  /**
   *     ========= Callback =============
   */
  const closeModal = useCallback((shouldFetch, data) => {
    _closeModal()
    if ((member || data) && !isEdit) {
      getData(data || member)
    }
    else if ((shouldFetch && typeof shouldFetch === 'boolean') || error) {
      getData()
    }
  }, [_closeModal, error, getData, isEdit, member])

  const onInfoNext = useCallback(() => {
    setError('')
    switch (identityType) {
      case IDENTITY_TYPES.SERVICE_ACCOUNT:
        if (!member || member?.tsg_id === tenant?.id) {
          setSidebarSelection(SIDEBAR_OPTIONS.CLIENT_CREDENTIALS)
        }
        else {
          if (rolesEnabled) {
            setSidebarSelection(SIDEBAR_OPTIONS.ASSIGN_ROLES)
          }
          else {
            closeModal()
          }
        }
        break
      default:
        if (rolesEnabled) {
          setSidebarSelection(SIDEBAR_OPTIONS.ASSIGN_ROLES)
        }
        else {
          closeModal()
        }
    }
  }, [closeModal, identityType, member, tenant?.id])

  const onCredsNext = useCallback(() => {
    setError('')
    if (rolesEnabled) {
      setSidebarSelection(SIDEBAR_OPTIONS.ASSIGN_ROLES)
    }
    else {
      closeModal()
    }
  }, [closeModal])

  const onCredsBack = useCallback(() => {
    setError('')
    setSidebarSelection(SIDEBAR_OPTIONS.IDENTITY_INFORMATION)
  }, [])

  const onAssignNext = useCallback((...props) => {
    setError('')
    closeModal(...props)
  }, [closeModal])

  const onAssignBack = useCallback(() => {
    setError('')
    switch (identityType) {
      case IDENTITY_TYPES.SERVICE_ACCOUNT:
        if (!member || member?.tsg_id === tenant?.id) {
          setSidebarSelection(SIDEBAR_OPTIONS.CLIENT_CREDENTIALS)
        }
        else {
          setSidebarSelection(SIDEBAR_OPTIONS.IDENTITY_INFORMATION)
        }
        break
      case IDENTITY_TYPES.USER:
      default:
        setSidebarSelection(SIDEBAR_OPTIONS.IDENTITY_INFORMATION)
    }
  }, [identityType, member, tenant?.id])

  const removeIdentity = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      await _removeIdentity(tenant?.id, [member])
      closeModal()
      getData()
    }
    catch (error) {
      setError(error.message || ERROR_SERVER_REMOVE)
    }
    setLoading(false)
  }, [_removeIdentity, closeModal, getData, member, tenant?.id])

  const onRemove = useCallback(async () => {
    removeModal.openModal({ onRemove: removeIdentity })
  }, [removeIdentity, removeModal])

  const modalBody = useMemo(() => {
    switch (sidebarSelection.name) {
      case LABEL_CLIENT_CREDENTIALS:
        return <ClientCredentials
          onBackPressed={onCredsBack}
          onNextPressed={onCredsNext}
          account={member}
          setAccount={setMember}
          isEdit={isEdit}
          {...{ tenant, onRemove, error, loading, length }}
        />
      case LABEL_ASSIGN_ROLES:
        return iamPhase2 ? (
          <AssignRoles
            onNextPressed={onAssignNext}
            onBackPressed={onAssignBack}
            isLoading={loading}
            {...{ isEdit, onRemove, tenant, member, setMember, closeModal, setLoading, error, setError, identityType, length }}
          />
        ) : (
          <AssignRolesOld
            onNextPressed={onAssignNext}
            onBackPressed={onAssignBack}
            isLoading={loading}
            {...{ isEdit, onRemove, tenant, member, setMember, closeModal, setLoading, error, setError, identityType, length }}
          />
        )
      case LABEL_IDENTITY_INFORMATION:
      default:
        return isEdit && iamPhase1 ? (
          <IdentityInformation
            member={member}
            onNextPressed={onInfoNext}
            isLoading={loading}
            onBackPressed={closeModal}
            {...{ onRemove, length }}
          />
        ) : (
          <IdentityInformationOld
            onNextClicked={onInfoNext}
            member={member}
            setMember={setMember}
            identityType={identityType}
            {...{ tenant, closeModal, setIdentityType, isEdit, onRemove, error, loading, length }}/>
        )
    }
  }, [closeModal, error, identityType, isEdit, loading, member, onAssignBack, onAssignNext, onCredsBack, onCredsNext, onInfoNext, onRemove, sidebarSelection.name, tenant, length])

  const sidebarOptions = useMemo(() => {
    const copy = Object.assign({}, SIDEBAR_OPTIONS)
    if (rolesEnabled) {
      copy.ASSIGN_ROLES.optional = false
    }
    if (identityType !== IDENTITY_TYPES.SERVICE_ACCOUNT || (!!member && member?.tsg_id !== tenant?.id)) {
      delete copy.CLIENT_CREDENTIALS
    }
    else {
      copy.CLIENT_CREDENTIALS.icon = !!member && (rolesEnabled ? sidebarSelection.name === SIDEBAR_OPTIONS.ASSIGN_ROLES.name : false)
      if (rolesEnabled) {
        copy.ASSIGN_ROLES.optional = true
      }
    }
    copy.IDENTITY_INFORMATION.icon = !!member && (sidebarSelection.name === SIDEBAR_OPTIONS.CLIENT_CREDENTIALS.name || (rolesEnabled ? sidebarSelection.name === SIDEBAR_OPTIONS.ASSIGN_ROLES.name : false))
    return Object.values(copy)
  }, [identityType, member, sidebarSelection.name, tenant?.id])

  const title = useMemo(() => {
    if (!isEdit) {
      return StringTemplate(LABEL_ADD_NEW_IDENTITY, [tenant?.display_name])
    }
    if (member?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT) {
      return StringTemplate(LABEL_EDIT_ACCOUNT, [member?.display_name || member?.name])
    }
    return StringTemplate(LABEL_VIEW_IDENTITY, [member?.display_name || member?.identity_email])
  }, [isEdit, member?.display_name, member?.identity_email, member?.identity_type, member?.name, tenant?.display_name])

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    if (member && !isEdit) {
      setSidebarSelection(identityType === IDENTITY_TYPES.SERVICE_ACCOUNT ? SIDEBAR_OPTIONS.CLIENT_CREDENTIALS : (rolesEnabled ? SIDEBAR_OPTIONS.ASSIGN_ROLES : closeModal()))
    }
  }, [closeModal, identityType, isEdit, member])

  return (
    <Modal
      size='xl'
      style={{ zIndex: 1051, top: '50%', '--tw-translate-y': '-50%', minHeight: '600px', minWidth: '800px', overflow: 'hidden' }}
      onClose={closeModal}
      isOpen={isOpen}
    >
      <div className="header-wrapper">
        <ModalHeader {...{ title }} />
      </div>
      <div className="tw-flex tw-flex-1 tw-relative tw-max-h-full">
        <Sidebar options={sidebarOptions} currentOption={sidebarSelection}/>
        <div className="tw-absolute tw-right-0 tw-bottom-0 tw-top-0" style={{ left: '200px' }}>
          {modalBody}
        </div>
      </div>
    </Modal>
  )
}

export default IdentityModal
